import { Group as RadixGroup } from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import {
  forwardRef,
  Children,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { useId } from '../../core/hooks/useId.js';
import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { MenuGroupContext } from './contexts/MenuGroupContext.js';
import { Label } from './Label.js';
import * as styles from './Menu.sty.js';

/**
 * React renders `null`, `undefined`, and booleans as nothing, and an empty
 * string leaves the heading blank — none of those can name the group. Every
 * other value can, including `0`.
 */
const isRenderableLabel = (label: ReactNode): boolean =>
  label !== null &&
  label !== undefined &&
  typeof label !== 'boolean' &&
  label !== '';

const appendLabelId =
  (labelId: string) =>
  (current: readonly string[]): string[] => [...current, labelId];

const removeLabelId =
  (labelId: string) =>
  (current: readonly string[]): string[] =>
    current.filter((registeredId) => registeredId !== labelId);

/**
 * @public
 */
export interface MenuGroupProps
  extends WithChildren, AriaLabelingProps, StylingProps, DataTestId {
  /**
   * A heading rendered above the group that also becomes its accessible name.
   * Prefer this over wiring a `Menu.Label` and `aria-labelledby` by hand.
   */
  label?: ReactNode;
}

/**
 * Used to group multiple items.
 * @public
 */
export const Group = /* @__PURE__ */ forwardRef<HTMLDivElement, MenuGroupProps>(
  (props, forwardedRef) => {
    const {
      children,
      label,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      'aria-details': ariaDetails,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
    }: MenuGroupProps = props;
    const generatedLabelId = useId();
    const [registeredLabelIds, setRegisteredLabelIds] = useState<string[]>([]);
    const hasLabelProp = isRenderableLabel(label);

    const registerLabel = useCallback((labelId: string) => {
      setRegisteredLabelIds(appendLabelId(labelId));
      return () => setRegisteredLabelIds(removeLabelId(labelId));
    }, []);

    const groupContext = useMemo(() => ({ registerLabel }), [registerLabel]);

    const hasChildren = Children.count(children) > 0;

    // An explicit name from the consumer always wins over an inferred one —
    // including an empty one, which is a deliberate choice to stay unnamed.
    const hasExplicitName =
      ariaLabel !== undefined || ariaLabelledBy !== undefined;
    // Labels register from their mount effects, which run in tree order, so the
    // first entry is the topmost label — a group with section headers between
    // its items is named by the heading that opens it, not by a later one.
    const inferredLabelledBy = hasLabelProp
      ? generatedLabelId
      : registeredLabelIds[0];

    return hasChildren ? (
      <>
        {hasLabelProp ? <Label id={generatedLabelId}>{label}</Label> : null}
        <MenuGroupContext.Provider value={groupContext}>
          <RadixGroup
            className={clsx(styles.group, consumerClassName)}
            ref={forwardedRef}
            aria-label={ariaLabel}
            aria-labelledby={
              hasExplicitName ? ariaLabelledBy : inferredLabelledBy
            }
            aria-describedby={ariaDescribedBy}
            aria-details={ariaDetails}
            data-testid={dataTestId}
            style={consumerStyle}
          >
            {children}
          </RadixGroup>
        </MenuGroupContext.Provider>
      </>
    ) : null;
  },
);

(Group as typeof Group & { displayName: string }).displayName = 'Menu.Group';
